// Kochmodus: großer Text, ein Schritt nach dem anderen, Bildschirm bleibt an.
// Beim Kochen hat man fettige Finger und kein Interesse an kleiner Schrift.
import { formatNumber } from '@speis/shared';
import type { RecipeView } from '@speis/shared';
import { state } from '../core/store.js';
import { clear, el, haptic } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

/** Zerlegt die Zubereitung in Schritte (nummeriert oder je Absatz). */
export function splitSteps(instructions: string): string[] {
  const text = instructions.trim();
  if (!text) return [];

  // „1. …  2. …" – auch ohne Zeilenumbrüche.
  const numbered = text
    .split(/\s*(?:\n|^)\s*\d+[.)]\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (numbered.length > 1) return numbered;

  const paragraphs = text
    .split(/\n{2,}|\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (paragraphs.length > 1) return paragraphs;

  // Ein Block: an Satzenden trennen, aber nicht bei „z.B." o.ä.
  const sentences = text
    .split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ])/)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.length > 1 ? sentences : [text];
}

export function openCookMode(recipe: RecipeView, servings: number): void {
  const steps = splitSteps(recipe.instructions ?? '');
  let index = 0;
  let wakeLock: WakeLockSentinelLike | null = null;

  const factor = servings / (recipe.servings || 1);
  const ingredients = el('ul', { class: 'ingredient-list cook-ingredients' });
  for (const i of recipe.ingredients) {
    const name = i.genericName ?? state.products.find((p) => p.id === i.productId)?.name ?? 'Zutat';
    const amount =
      i.amount != null ? `${formatNumber(i.amount * factor)} ${i.unit ?? ''}`.trim() : '·';
    ingredients.append(
      el('li', {}, [el('span', { class: 'ing-amount tabular' }, amount), el('span', {}, name)]),
    );
  }

  const stepBox = el('div', { class: 'cook-step', 'aria-live': 'polite' });
  const progress = el('p', { class: 'tiny' });

  const body = el('div', { class: 'cook-mode' }, [
    el('details', { class: 'cook-ingredients-wrap' }, [
      el('summary', {}, `Zutaten für ${servings} Portionen`),
      ingredients,
    ]),
    stepBox,
    progress,
  ]);

  const sheet = openSheet(body, {
    title: recipe.name,
    onClose: () => void releaseLock(),
  });

  function draw(): void {
    clear(stepBox);
    if (!steps.length) {
      stepBox.append(
        el('p', { class: 'muted' }, 'Für dieses Rezept ist keine Zubereitung hinterlegt.'),
      );
      progress.textContent = '';
      return;
    }
    stepBox.append(
      el('span', { class: 'cook-step-num' }, `Schritt ${index + 1}`),
      el('p', { class: 'cook-step-text' }, steps[index]!),
    );
    progress.textContent = `${index + 1} von ${steps.length}`;
    prev.disabled = index === 0;
    next.disabled = index >= steps.length - 1;
  }

  const prev = el(
    'button',
    {
      class: 'btn btn-secondary',
      type: 'button',
      onclick: () => {
        index = Math.max(0, index - 1);
        haptic(6);
        draw();
      },
    },
    [icon('chevronLeft', { size: 18 }), 'Zurück'],
  );

  const next = el(
    'button',
    {
      class: 'btn btn-primary',
      type: 'button',
      onclick: () => {
        index = Math.min(steps.length - 1, index + 1);
        haptic(10);
        draw();
      },
    },
    ['Weiter', icon('chevronRight', { size: 18 })],
  );

  sheet.addActions(el('div', { class: 'btn-row' }, [prev, next]));
  draw();

  // Bildschirm wach halten, solange der Kochmodus offen ist.
  void requestLock();

  async function requestLock(): Promise<void> {
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
      };
      wakeLock = (await nav.wakeLock?.request('screen')) ?? null;
    } catch {
      /* nicht unterstützt oder abgelehnt – dann eben nicht */
    }
  }

  async function releaseLock(): Promise<void> {
    try {
      await wakeLock?.release();
    } catch {
      /* egal */
    }
    wakeLock = null;
  }
}
